'use client';

import React, { useState } from 'react';
import { CalloutBoxComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { X } from 'lucide-react';

interface CalloutBoxProps {
  component: CalloutBoxComponent;
}

export const CalloutBox: React.FC<CalloutBoxProps> = ({ component }) => {
  const { props } = component;
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) return null;

  const getCalloutStyles = () => {
    switch (props.type) {
      case 'tip':
        // Muted Sage - natural learning insights
        return {
          container: 'bg-learn-sage-50 border-learn-sage-300',
          icon: 'lightbulb' as const,
          iconColor: 'text-learn-sage-600',
          titleColor: 'text-learn-sage-900',
          textColor: 'text-learn-sage-800',
          label: '💡 Tip',
        };
      case 'warning':
        // Muted Honey - gentle important attention
        return {
          container: 'bg-learn-honey-50 border-learn-honey-300',
          icon: 'warning' as const,
          iconColor: 'text-learn-honey-600',
          titleColor: 'text-learn-honey-900',
          textColor: 'text-learn-honey-800',
          label: '⚠️ Important',
        };
      case 'example':
        // Soft Coral - engaging examples
        return {
          container: 'bg-learn-coral-50 border-learn-coral-300',
          icon: 'star' as const,
          iconColor: 'text-learn-coral-600',
          titleColor: 'text-learn-coral-900',
          textColor: 'text-learn-coral-800',
          label: '✨ Example',
        };
      case 'key-takeaway':
        // Muted Clay - memorable key points
        return {
          container: 'bg-learn-clay-50 border-learn-clay-300',
          icon: 'target' as const,
          iconColor: 'text-learn-clay-600',
          titleColor: 'text-learn-clay-900',
          textColor: 'text-learn-clay-800',
          label: '🎯 Key Takeaway',
        };
      case 'did-you-know':
        // Muted Sky Blue - calm information
        return {
          container: 'bg-learn-sky-50 border-learn-sky-300',
          icon: 'info' as const,
          iconColor: 'text-learn-sky-600',
          titleColor: 'text-learn-sky-900',
          textColor: 'text-learn-sky-800',
          label: '💭 Did You Know?',
        };
      default:
        // Very soft neutral
        return {
          container: 'bg-learn-sage-50 border-learn-sage-200',
          icon: 'info' as const,
          iconColor: 'text-learn-sage-600',
          titleColor: 'text-gray-900',
          textColor: 'text-gray-800',
          label: 'Note',
        };
    }
  };

  const styles = getCalloutStyles();

  return (
    <div className={`w-full border-l-4 ${styles.container} rounded-r-xl p-4 md:p-5 shadow-sm relative transition-all duration-200 hover:shadow-md`}>
      {/* Dismiss button */}
      {props.dismissible && (
        <button
          onClick={() => setIsDismissed(true)}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <ComponentIcon 
            icon={props.icon || styles.icon} 
            className={`w-5 h-5 ${styles.iconColor}`} 
          />
        </div>

        {/* Content */}
        <div className="flex-1">
          {props.title ? (
            <h4 className={`text-base font-bold ${styles.titleColor} mb-2`}>
              {props.title}
            </h4>
          ) : (
            <div className={`text-xs font-semibold ${styles.titleColor} uppercase tracking-wider mb-2`}>
              {styles.label}
            </div>
          )}
          <p className={`text-sm ${styles.textColor} leading-relaxed whitespace-pre-line`}>
            {props.content}
          </p>
        </div>
      </div>
    </div>
  );
};

